import { Box, CircularProgress, Typography } from '@mui/material';
import * as d3 from 'd3';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import oroborosGraphLogo from '../../assets/oroboros_logo_graph.png';
import graphBackgroundPattern from '../../assets/patterns/dots_6_percent_512px.png';
import '../graph.css';

const OXYGEN_COLOR = '#2C0DF1';
const COLOR_STROKE = '#CCD3D7';
const COLOR_TOP = '#9ABAC6';
const COLOR_HANDLE = '#9ABAC6';
const COLOR_SELECTED = '#00CD93';
const COLOR_TEXT = '#394B57';
const HANDLE_WIDTH = 6;
const HANDLE_HEIGHT = 22;
const HANDLE_CORNER_RADIUS = 3;
const MIN_PIXEL_WIDTH = 4; // minimum width in pixels when creating/resizing

/**
 * O2kChartView — decoupled O2K chart that receives all data via props.
 * No global store access. Used by O2KineticsGrid for N-chamber support.
 */
const O2kChartView = ({
  height = '100%',
  chamber = '1',
  controlsRef,
  // Data props
  chamberData = null,
  transitions: transitionsProp = [],
  transitionsLoading = false,
  selectedTransitionId = null,
  // Callbacks
  onAddTransition,
  onUpdateTransition,
  onRemoveTransition,
  onSelectTransition,
}) => {
  const currentFileData = chamberData;
  const transitions = transitionsProp;

  // Wrap callbacks to match the old store API signatures
  const addTransition = useCallback((chamber, t) => onAddTransition?.(t), [onAddTransition]);
  const updateTransition = useCallback(
    (chamber, id, updater) => onUpdateTransition?.(id, updater),
    [onUpdateTransition],
  );
  const removeTransition = useCallback(
    (chamber, id) => onRemoveTransition?.(id),
    [onRemoveTransition],
  );
  const setSelectedTransition = useCallback(
    (chamber, id) => onSelectTransition?.(id),
    [onSelectTransition],
  );

  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const svgRef = useRef(null);

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Separate transforms for x and y axes to support uneven zoom/distortion
  const [xTransform, setXTransform] = useState({ k: 1, translate: 0 });
  const [yTransform, setYTransform] = useState({ k: 1, translate: 0 });
  const [yDomainOverride, setYDomainOverride] = useState(null);
  const [draft, setDraft] = useState(null); // {start: Date, end: Date}
  const selectedId = selectedTransitionId;
  const [transitionsLocked, setTransitionsLocked] = useState(false);

  const rafIdRef = useRef(0);
  const xTransformRef = useRef({ k: 1, translate: 0 });
  const yTransformRef = useRef({ k: 1, translate: 0 });
  const draftRef = useRef(null);
  const panLockRef = useRef(null); // null | 'x' (lock x → vertical-only) | 'y' (lock y → horizontal-only)
  const isDraggingRef = useRef(false); // true during active pan/drag (not wheel zoom)
  const pendingXTransformRef = useRef(null); // holds x transform during drag without React updates
  const pendingYTransformRef = useRef(null); // holds y transform during drag without React updates
  const lastAxisUpdateRef = useRef(0); // timestamp of last axis update (for throttling)
  const updateSVGAxesRef = useRef(null); // stable ref to updateSVGAxes for use in zoom handler
  const updateTransitionPositionsRef = useRef(null); // stable ref to updateTransitionPositions for use in zoom handler
  const renderSVGRef = useRef(null); // stable ref to renderSVG for zoom end
  const baseXScaleRef = useRef(null); // stable ref to baseXScale for direct rendering
  const baseYScaleRef = useRef(null); // stable ref to baseYScale for direct rendering
  const dataRef = useRef([]); // stable ref to data for direct rendering
  const transitionsRef = useRef([]); // stable ref to transitions for direct rendering
  const selectedIdRef = useRef(null); // stable ref to selectedId for direct rendering
  const dragStartRef = useRef({
    x: 0,
    y: 0,
    xTransform: { k: 1, translate: 0 },
    yTransform: { k: 1, translate: 0 },
  });
  // Unique IDs for defs/clipPaths/patterns to avoid DOM collisions across multiple graphs
  const clipIdRef = useRef(`o2k-clip-${Math.random().toString(36).slice(2)}`);
  const groupClipIdRef = useRef(`o2k-group-clip-${Math.random().toString(36).slice(2)}`);
  const topClipIdRef = useRef(`o2k-top-clip-${Math.random().toString(36).slice(2)}`);
  const patternIdRef = useRef(`o2k-bg-pattern-${Math.random().toString(36).slice(2)}`);
  const transitionGradientIdRef = useRef(
    `o2k-transition-gradient-${Math.random().toString(36).slice(2)}`,
  );
  const selectedTransitionGradientIdRef = useRef(
    `o2k-transition-gradient-selected-${Math.random().toString(36).slice(2)}`,
  );

  // Keep refs in sync
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);
  useEffect(() => {
    xTransformRef.current = xTransform;
  }, [xTransform]);
  useEffect(() => {
    yTransformRef.current = yTransform;
  }, [yTransform]);

  // Data → [{time: Date, oxygen: number}]
  const data = useMemo(() => {
    if (!currentFileData?.timeSeries || !currentFileData?.oxygenSeries) return [];
    const { timeSeries, oxygenSeries } = currentFileData;
    const n = Math.min(timeSeries.length, oxygenSeries.length);
    return Array.from({ length: n }, (_, i) => ({
      time: new Date(timeSeries[i] * 1000),
      oxygen: oxygenSeries[i],
    }));
  }, [currentFileData]);

  // Keep data ref in sync (must be after data is defined)
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Layout
  const margin = { top: 36, right: 80, bottom: 40, left: 70 };
  const plotWidth = Math.max(0, dimensions.width - margin.left - margin.right);
  const plotHeight = Math.max(0, dimensions.height - margin.top - margin.bottom);

  // Loading state for transitions
  const isLoading = transitionsLoading;

  // Scales
  const baseXScale = useMemo(() => {
    if (!data.length) return d3.scaleUtc().domain([new Date(), new Date()]).range([0, plotWidth]);
    const domain = d3.extent(data, (d) => d.time);
    return d3.scaleUtc().domain(domain).range([0, plotWidth]);
  }, [data, plotWidth]);

  const baseYScale = useMemo(() => {
    if (!data.length) return d3.scaleLinear().domain([0, 1]).range([plotHeight, 0]);
    if (yDomainOverride) return d3.scaleLinear().domain(yDomainOverride).range([plotHeight, 0]);
    const yMin = d3.min(data, (d) => d.oxygen);
    const yMax = d3.max(data, (d) => d.oxygen);
    const pad = Math.max(1e-6, (yMax - yMin) * 0.05);
    return d3
      .scaleLinear()
      .domain([yMin - pad, yMax + pad])
      .nice()
      .range([plotHeight, 0]);
  }, [data, plotHeight, yDomainOverride]);

  // Transformed scales for rendering (using separate x and y transforms)
  const xScale = useMemo(() => {
    const [d0, d1] = baseXScale.domain();
    const [r0, r1] = baseXScale.range();
    const k = xTransform.k;
    const tx = xTransform.translate;
    const newD0 = baseXScale.invert((baseXScale(d0) - tx) / k);
    const newD1 = baseXScale.invert((baseXScale(d1) - tx) / k);
    return d3.scaleUtc().domain([newD0, newD1]).range([r0, r1]);
  }, [baseXScale, xTransform]);

  const yScale = useMemo(() => {
    const [d0, d1] = baseYScale.domain();
    const [r0, r1] = baseYScale.range();
    const k = yTransform.k;
    const ty = yTransform.translate;
    const newD0 = baseYScale.invert((baseYScale(d0) - ty) / k);
    const newD1 = baseYScale.invert((baseYScale(d1) - ty) / k);
    return d3.scaleLinear().domain([newD0, newD1]).range([r0, r1]);
  }, [baseYScale, yTransform]);

  // Keep scale refs in sync (must be after scales are defined)
  useEffect(() => {
    baseXScaleRef.current = baseXScale;
  }, [baseXScale]);
  useEffect(() => {
    baseYScaleRef.current = baseYScale;
  }, [baseYScale]);

  // Keep transitions and selectedId refs in sync
  useEffect(() => {
    transitionsRef.current = transitions;
  }, [transitions]);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  // Viewport clipping and downsampling for performance
  const visibleData = useMemo(() => {
    if (!data.length || plotWidth <= 0) return data;

    // Get visible domain from current scales
    const [xMin, xMax] = xScale.domain();

    // Use binary search to find visible range efficiently
    const bisector = d3.bisector((d) => d.time).left;
    const startIdx = Math.max(0, bisector(data, xMin) - 1); // Include 1 point before viewport
    const endIdx = Math.min(data.length, bisector(data, xMax) + 1); // Include 1 point after viewport

    const visibleSlice = data.slice(startIdx, endIdx);

    // Downsample if we have more than 2 points per pixel
    // This prevents rendering unnecessary detail that can't be seen
    const maxPoints = plotWidth * 2;

    if (visibleSlice.length <= maxPoints) {
      return visibleSlice;
    }

    // Simple decimation: keep every Nth point
    const step = Math.ceil(visibleSlice.length / maxPoints);
    const downsampled = [];

    // Always keep first and last points of visible range
    downsampled.push(visibleSlice[0]);

    for (let i = step; i < visibleSlice.length - 1; i += step) {
      downsampled.push(visibleSlice[i]);
    }

    // Always keep the last point
    if (visibleSlice.length > 1) {
      downsampled.push(visibleSlice[visibleSlice.length - 1]);
    }

    return downsampled;
  }, [data, xScale, plotWidth]);

  // ═══════════════════════════════════════════════════════════════════════════
  // HELPER FUNCTIONS - Canvas rendering & visible data computation
  // ═══════════════════════════════════════════════════════════════════════════

  // Helper: Compute visible & downsampled data for given scales
  const computeVisibleData = useCallback((xScale, data, plotWidth) => {
    if (!data.length || plotWidth <= 0) return data;

    const [xMin, xMax] = xScale.domain();
    const bisector = d3.bisector((d) => d.time).left;
    const startIdx = Math.max(0, bisector(data, xMin) - 1);
    const endIdx = Math.min(data.length, bisector(data, xMax) + 1);
    const visibleSlice = data.slice(startIdx, endIdx);

    const maxPoints = plotWidth * 2;
    if (visibleSlice.length <= maxPoints) return visibleSlice;

    const step = Math.ceil(visibleSlice.length / maxPoints);
    const downsampled = [visibleSlice[0]];
    for (let i = step; i < visibleSlice.length - 1; i += step) {
      downsampled.push(visibleSlice[i]);
    }
    if (visibleSlice.length > 1) {
      downsampled.push(visibleSlice[visibleSlice.length - 1]);
    }
    return downsampled;
  }, []);

  // Helper: Render canvas with given data and scales
  const drawCanvasTrace = useCallback(
    (canvas, renderData, xScale, yScale) => {
      if (!canvas || !renderData.length || plotWidth <= 0 || plotHeight <= 0) return;

      const ctx = canvas.getContext('2d');
      const DPR = window.devicePixelRatio || 1;

      canvas.width = Math.max(1, Math.floor(dimensions.width * DPR));
      canvas.height = Math.max(1, Math.floor(dimensions.height * DPR));
      canvas.style.width = `${dimensions.width}px`;
      canvas.style.height = `${dimensions.height}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(DPR, DPR);
      ctx.clearRect(0, 0, dimensions.width, dimensions.height);

      ctx.save();
      ctx.beginPath();
      ctx.rect(margin.left, margin.top, plotWidth, plotHeight);
      ctx.clip();
      ctx.translate(margin.left, margin.top);

      ctx.beginPath();
      ctx.strokeStyle = OXYGEN_COLOR;
      ctx.lineWidth = 2;
      let first = true;
      for (let i = 0; i < renderData.length; i++) {
        const x = xScale(renderData[i].time);
        const y = yScale(renderData[i].oxygen);
        if (Number.isFinite(x) && Number.isFinite(y)) {
          if (first) {
            ctx.moveTo(x, y);
            first = false;
          } else {
            ctx.lineTo(x, y);
          }
        }
      }
      ctx.stroke();
      ctx.restore();
    },
    [dimensions, plotWidth, plotHeight, margin],
  );

  // Helper: Update transition positions in SVG
  const updateTransitionPositions = useCallback(
    (xScale, transitions, draft, selectedId) => {
      try {
        const svg = d3.select(svgRef.current);
        const g = svg.select('g');
        if (g.empty()) return;

        const transitionsLayer = g.select('.transitions-layer');
        const transitionsTopLayer = g.select('.transitions-top-layer');
        if (transitionsLayer.empty() || transitionsTopLayer.empty()) return;

        // Get all transitions including draft
        const toRender = [...(Array.isArray(transitions) ? transitions : [])];
        if (draft && draft.start && draft.end) {
          toRender.push({ id: '__draft__', name: '', start: draft.start, end: draft.end });
        }

        // Update each transition
        transitionsLayer.selectAll('g.transition').each(function () {
          const el = d3.select(this);
          const transitionId = el.attr('data-id');
          const t = toRender.find((tr) => String(tr.id) === transitionId);
          if (!t) return;

          const x1 = xScale(new Date(t.start));
          const x2 = xScale(new Date(t.end ?? t.start));
          const left = Math.min(x1, x2);
          const width = Math.max(1, Math.abs(x2 - x1));

          // Update transition body rect
          el.select('rect.transition-body').attr('x', left).attr('width', width);

          // Update left and right stroke lines
          const lines = el.selectAll('line');
          lines
            .filter((_, i) => i === 0)
            .attr('x1', left)
            .attr('x2', left);
          lines
            .filter((_, i) => i === 1)
            .attr('x1', left + width)
            .attr('x2', left + width);

          // Update handles
          const handles = el.selectAll('rect.resize-handle');
          handles.filter((_, i) => i === 0).attr('x', left - HANDLE_WIDTH / 2); // left handle
          handles.filter((_, i) => i === 1).attr('x', left + width - HANDLE_WIDTH / 2); // right handle

          // Update text label
          el.select('text').attr('x', left + 6);
        });

        // Rebuild top badges based on transitions (simpler than trying to update existing ones)
        transitionsTopLayer.selectAll('rect').remove();
        toRender.forEach((t) => {
          const x1 = xScale(new Date(t.start));
          const x2 = xScale(new Date(t.end ?? t.start));
          const left = Math.min(x1, x2);
          const width = Math.max(2, Math.abs(x2 - x1));
          const topHeight = 4;
          const topY = -topHeight;
          const isSelected = t.id === selectedId;
          const topColor = isSelected ? COLOR_SELECTED : COLOR_TOP;

          transitionsTopLayer
            .append('rect')
            .attr('data-id', t.id)
            .attr('x', left)
            .attr('y', topY)
            .attr('width', width)
            .attr('height', topHeight)
            .attr('fill', topColor)
            .attr('stroke', topColor)
            .attr('opacity', 1);
        });
      } catch (e) {
        // Silently fail if SVG structure not ready
      }
    },
    [plotHeight],
  );

  // Helper functions
  const findAtX = useCallback((x) => xScale.invert(x), [xScale]);

  const minDurationMs = useMemo(() => {
    const minPx = MIN_PIXEL_WIDTH;
    const t0 = findAtX(0).getTime();
    const t1 = findAtX(minPx).getTime();
    return Math.abs(t1 - t0);
  }, [findAtX]);

  const overlapsAny = useCallback(
    (candidate, ignoreId = null) => {
      const aStart = Math.min(candidate.start.getTime(), candidate.end.getTime());
      const aEnd = Math.max(candidate.start.getTime(), candidate.end.getTime());
      return transitions.some((m) => {
        if (ignoreId && m.id === ignoreId) return false;
        const bStart = new Date(m.start).getTime();
        const bEnd = new Date(m.end ?? m.start).getTime();
        return !(aEnd <= bStart || aStart >= bEnd);
      });
    },
    [transitions],
  );

  const getSelected = useCallback(
    (evt) => {
      const svgEl = svgRef.current;
      if (!svgEl) return null;
      const [mx] = d3.pointer(evt, svgEl);
      const localX = Math.min(plotWidth, Math.max(0, mx - margin.left));
      let hitId = null;
      (Array.isArray(transitions) ? transitions : []).forEach((t) => {
        const x1 = xScale(new Date(t.start));
        const x2 = xScale(new Date(t.end ?? t.start));
        const left = Math.min(x1, x2);
        const width = Math.max(1, Math.abs(x2 - x1));
        if (localX >= left && localX <= left + width) hitId = t.id;
      });
      return hitId;
    },
    [xScale, plotWidth, margin.left, transitions],
  );

  // Resize observer
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width: Math.round(width), height: Math.round(height) });
    });
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setDimensions({ width: Math.round(rect.width), height: Math.round(rect.height) });
    }
    ro.observe(el);
    return () => ro.disconnect();
  }, [currentFileData]);

  // Canvas rendering (oxygen trace) - uses helper function
  const renderCanvas = useCallback(() => {
    drawCanvasTrace(canvasRef.current, visibleData, xScale, yScale);
  }, [visibleData, xScale, yScale, drawCanvasTrace]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDERING - Canvas & SVG
  // ═══════════════════════════════════════════════════════════════════════════

  // Lightweight update: Only update axes/grids/labels without rebuilding entire SVG
  const updateSVGAxes = useCallback(
    (xScale, yScale) => {
      const svgEl = svgRef.current;
      if (!svgEl) return;

      const svg = d3.select(svgEl);
      const g = svg.select('g');
      if (g.empty()) return; // SVG not initialized yet

      // Update X-axis
      const xAxis = d3.axisBottom(xScale).tickFormat(d3.utcFormat('%H:%M:%S'));
      const xAxisGroup = g.select('.x-axis');
      if (!xAxisGroup.empty()) {
        xAxisGroup.call(xAxis);
        xAxisGroup.selectAll('.tick text').attr('dy', '12px');
      }

      // Update Y-axes (left and right)
      const yAxis = d3.axisLeft(yScale);
      const yAxisRight = d3.axisRight(yScale);
      const yAxisGroups = g.selectAll('.y-axis');
      yAxisGroups.each(function (d, i) {
        const el = d3.select(this);
        if (i === 0) {
          el.call(yAxis);
          el.selectAll('.tick text').attr('dx', '-4px');
        } else {
          el.call(yAxisRight);
          el.selectAll('.tick text').attr('dx', '4px');
        }
      });

      // Update visible range label
      const visStart = xScale.invert(0);
      const visEnd = xScale.invert(plotWidth);
      let durationLabel = '';
      if (visStart && visEnd) {
        const ms = Math.abs(visEnd - visStart);
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const hh = String(hours).padStart(2, '0');
        const mm = String(minutes).padStart(2, '0');
        const ss = String(seconds).padStart(2, '0');
        durationLabel = `${hh}:${mm}:${ss}`;
      }
      const rangeLabel = svg.select('.visible-range-label');
      if (!rangeLabel.empty()) {
        rangeLabel.text(`${durationLabel} visible range [hh:mm:ss]`);
      }
    },
    [plotWidth, plotHeight],
  );

  // SVG rendering (axes + transitions)
  const renderSVG = useCallback(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const svg = d3.select(svgEl);
    svg.selectAll('*').remove();

    svg
      .attr('width', dimensions.width)
      .attr('height', dimensions.height)
      .style('user-select', 'none')
      .style('cursor', 'default')
      .style('outline', 'none')
      .style('border', 'none')
      .style('-webkit-tap-highlight-color', 'transparent')
      .attr('focusable', 'false')
      .attr('tabindex', '-1');

    const clipId = clipIdRef.current;
    const groupClipId = groupClipIdRef.current;
    const topClipId = topClipIdRef.current;
    const defs = svg.append('defs');
    defs
      .append('clipPath')
      .attr('id', clipId)
      .append('rect')
      .attr('width', plotWidth)
      .attr('height', plotHeight)
      .attr('x', margin.left)
      .attr('y', margin.top);
    defs
      .append('clipPath')
      .attr('id', groupClipId)
      .append('rect')
      .attr('width', plotWidth)
      .attr('height', plotHeight)
      .attr('x', 0)
      .attr('y', 0);

    // Clip-path for small top band (allows a small badge above the plot)
    const topBand = 12;
    defs
      .append('clipPath')
      .attr('id', topClipId)
      .append('rect')
      .attr('width', plotWidth)
      .attr('height', topBand)
      .attr('x', 0)
      .attr('y', -topBand);

    // Background pattern
    const pattern = defs
      .append('pattern')
      .attr('id', patternIdRef.current)
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', 512)
      .attr('height', 512);
    pattern
      .append('image')
      .attr('xlink:href', graphBackgroundPattern)
      .attr('width', 512)
      .attr('height', 512);

    const xAxis = d3.axisBottom(xScale).tickFormat(d3.utcFormat('%H:%M:%S'));
    const yAxis = d3.axisLeft(yScale);
    const yAxisRight = d3.axisRight(yScale);

    // Base background rect with clip (behind everything)
    svg
      .append('rect')
      .attr('class', 'base-background')
      .attr('width', plotWidth)
      .attr('height', plotHeight)
      .attr('x', margin.left)
      .attr('y', margin.top)
      .attr('clip-path', `url(#${clipId})`);
    // Pattern fill
    svg
      .append('rect')
      .attr('width', plotWidth)
      .attr('height', plotHeight)
      .attr('x', margin.left)
      .attr('y', margin.top)
      .attr('fill', `url(#${patternIdRef.current})`)
      .attr('clip-path', `url(#${clipId})`);
    // Centered logo
    svg
      .append('image')
      .attr('xlink:href', oroborosGraphLogo)
      .attr('width', 200)
      .attr('height', 194)
      .attr('x', margin.left + plotWidth / 2 - 200 / 2)
      .attr('y', margin.top + plotHeight / 2 - 194 / 2)
      .attr('clip-path', `url(#${clipId})`);

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // defer axes drawing until after transitions so axes stay on top

    // Interaction overlay BEHIND transitions (so transitions can receive events)
    const overlay = g
      .append('rect')
      .attr('class', 'overlay')
      .attr('width', plotWidth)
      .attr('height', plotHeight)
      .style('fill', 'none')
      .style('pointer-events', 'all');

    // Transitions layer (above overlay) and a separate top-badge layer
    const transitionsLayer = g
      .append('g')
      .attr('class', 'transitions-layer')
      .attr('clip-path', `url(#${groupClipId})`);
    const transitionsTopLayer = g
      .append('g')
      .attr('class', 'transitions-top-layer')
      .attr('clip-path', `url(#${topClipId})`);

    // Define vertical gradient for transition fill
    const transitionGradient = defs
      .append('linearGradient')
      .attr('id', transitionGradientIdRef.current)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');
    transitionGradient
      .append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#4E798C')
      .attr('stop-opacity', 0.13);
    transitionGradient
      .append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#C3D7DE')
      .attr('stop-opacity', 0.05);

    // Shamrock gradient when selected
    const selectedTransitionGradient = defs
      .append('linearGradient')
      .attr('id', selectedTransitionGradientIdRef.current)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');
    selectedTransitionGradient
      .append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#20E6A8')
      .attr('stop-opacity', 0.5);
    selectedTransitionGradient
      .append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#B8F5E3')
      .attr('stop-opacity', 0.3);

    const drawHandle = (group, xPos, side, color) => {
      // Center vertically within plotHeight
      const yCenter = plotHeight / 2;
      const yTop = Math.max(0, yCenter - HANDLE_HEIGHT / 2);
      group
        .append('rect')
        .attr('class', 'resize-handle')
        .attr('data-side', side)
        .attr('x', xPos - HANDLE_WIDTH / 2)
        .attr('y', yTop)
        .attr('width', HANDLE_WIDTH)
        .attr('height', HANDLE_HEIGHT)
        .attr('rx', HANDLE_CORNER_RADIUS)
        .attr('ry', HANDLE_CORNER_RADIUS)
        .attr('fill', color)
        .attr('cursor', transitionsLocked ? 'not-allowed' : 'ew-resize');
    };

    // Render transitions
    const toRender = [...(Array.isArray(transitions) ? transitions : [])];
    if (draft && draft.start && draft.end) {
      toRender.push({ id: '__draft__', name: '', start: draft.start, end: draft.end });
    }
    toRender.forEach((t) => {
      try {
        const x1 = xScale(new Date(t.start));
        const x2 = xScale(new Date(t.end ?? t.start));
        const left = Math.min(x1, x2);
        const width = Math.max(1, Math.abs(x2 - x1));
        const group = transitionsLayer
          .append('g')
          .attr('class', 'transition')
          .attr('data-id', t.id);
        const isSelected = t.id === selectedId;
        const strokeColor = isSelected ? COLOR_SELECTED : COLOR_STROKE;
        const textColor = isSelected ? COLOR_SELECTED : COLOR_TEXT;
        const handleColor = isSelected ? COLOR_SELECTED : COLOR_HANDLE;
        const topColor = isSelected ? COLOR_SELECTED : COLOR_TOP;
        const rect = group
          .append('rect')
          .attr('class', 'transition-body')
          .attr('x', left)
          .attr('y', 0)
          .attr('width', width)
          .attr('height', plotHeight)
          .attr(
            'fill',
            t.id === '__draft__'
              ? 'rgba(32, 230, 168, 0.08)'
              : `url(#${isSelected ? selectedTransitionGradientIdRef.current : transitionGradientIdRef.current})`,
          )
          .attr('cursor', transitionsLocked ? 'default' : 'pointer')
          .style('pointer-events', t.id === '__draft__' ? 'none' : 'all');

        // Click selection on the mark body
        if (t.id !== '__draft__') {
          rect.on('click', () => {
            setSelectedTransition(chamber, t.id);
          });
        }

        // Left and right stroke lines
        group
          .append('line')
          .attr('x1', left)
          .attr('x2', left)
          .attr('y1', 0)
          .attr('y2', plotHeight)
          .attr('stroke', strokeColor)
          .attr('stroke-width', 1);
        group
          .append('line')
          .attr('x1', left + width)
          .attr('x2', left + width)
          .attr('y1', 0)
          .attr('y2', plotHeight)
          .attr('stroke', strokeColor)
          .attr('stroke-width', 1);

        // Add drag functionality directly to rect (like PlotPanelSample)
        if (t.id !== '__draft__') {
          rect.on('mousedown', (event) => {
            if (transitionsLocked) return; // locked: disable drag
            if (!event.shiftKey) return;
            event.stopPropagation();
            event.preventDefault();
            setSelectedTransition(chamber, t.id);
            const currentLeft = parseFloat(rect.attr('x')) || left;
            const [mx0] = d3.pointer(event, svgEl);
            const offset = mx0 - margin.left - currentLeft;

            const onMove = (ev) => {
              const [mx] = d3.pointer(ev, svgEl);
              const newLeft = Math.max(0, Math.min(plotWidth - width, mx - margin.left - offset));
              const newStart = xScale.invert(newLeft);
              const newEnd = xScale.invert(newLeft + width);
              const candidate = { start: newStart, end: newEnd };
              if (!overlapsAny(candidate, t.id)) {
                updateTransition(chamber, t.id, { start: newStart, end: newEnd });
              }
            };
            const onUp = () => {
              window.removeEventListener('mousemove', onMove);
              window.removeEventListener('mouseup', onUp);
            };
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
          });
        }

        if (t.id !== '__draft__') {
          drawHandle(group, left, 'left', handleColor);
          drawHandle(group, left + width, 'right', handleColor);
        }
        // Transition label
        if (t.name) {
          group
            .append('text')
            .attr('x', left + 6)
            .attr('y', Math.max(12, plotHeight - 8))
            .style('fill', textColor)
            .style('font-size', '12px')
            .style('user-select', 'none')
            .text(String(t.name));
        }

        // Small top badge above the plot area (clipped to top band)
        const topWidth = Math.max(2, width);
        const topHeight = 4;
        const topX = left;
        const topY = -topHeight;
        transitionsTopLayer
          .append('rect')
          .attr('x', topX)
          .attr('y', topY)
          .attr('width', topWidth)
          .attr('height', topHeight)
          .attr('fill', topColor)
          .attr('stroke', topColor)
          .attr('opacity', 1);
      } catch {}
    });

    // Draw axes last so they appear above transitions; disable pointer events
    g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${plotHeight})`)
      .style('pointer-events', 'none')
      .call(xAxis)
      .selectAll('.tick text')
      .attr('dy', '12px');

    g.append('g')
      .attr('class', 'y-axis')
      .style('pointer-events', 'none')
      .call(yAxis)
      .selectAll('.tick text')
      .attr('dx', '-4px');

    g.append('g')
      .attr('class', 'y-axis')
      .attr('transform', `translate(${plotWidth},0)`)
      .style('pointer-events', 'none')
      .call(yAxisRight)
      .selectAll('.tick text')
      .attr('dx', '4px');

    // Y-axis labels (left and right)
    g.append('text')
      .attr('class', 'y-axis-label')
      .attr('transform', `translate(${-margin.left + 12}, ${plotHeight / 2}) rotate(-90)`)
      .attr('text-anchor', 'middle')
      .text('O₂ concentration [µM]');

    g.append('text')
      .attr('class', 'y-axis-label')
      .attr(
        'transform',
        `translate(${plotWidth + margin.right - 16}, ${plotHeight / 2}) rotate(-90)`,
      )
      .attr('text-anchor', 'middle')
      .text('O₂ concentration [µM]');

    // Visible time span label centered above the plot
    const visStart = xScale.invert(0);
    const visEnd = xScale.invert(plotWidth);
    let durationLabel = '';
    if (visStart && visEnd) {
      const ms = Math.abs(visEnd - visStart);
      const totalSeconds = Math.floor(ms / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const hh = String(hours).padStart(2, '0');
      const mm = String(minutes).padStart(2, '0');
      const ss = String(seconds).padStart(2, '0');
      durationLabel = `${hh}:${mm}:${ss}`;
    }

    svg
      .append('text')
      .attr('class', 'visible-range-label')
      .attr('x', margin.left + plotWidth / 2)
      .attr('y', Math.max(12, margin.top - 16))
      .attr('text-anchor', 'middle')
      .text(`${durationLabel} visible range [hh:mm:ss]`);

    // Create via Ctrl + drag
    overlay.on('mousedown', (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.button !== 0) {
        // Prevent default behavior on shift+click to avoid focus outline
        if (event.shiftKey) {
          event.preventDefault();
          event.stopPropagation();
        }
        // Select on simple click only if not dragging/panning
        const id = getSelected(event);
        let hasMoved = false;

        const onMove = () => {
          hasMoved = true;
        };
        const onUp = () => {
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
          // Only change selection if this was a click (not a drag/pan)
          if (!hasMoved) {
            setSelectedTransition(chamber, id);
          }
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return;
      }
      event.preventDefault();
      // If locked, prevent creating a new transition
      if (transitionsLocked) return;
      const [mx] = d3.pointer(event, svgEl);
      const startTime = xScale.invert(Math.min(plotWidth, Math.max(0, mx - margin.left)));

      // Check if starting point overlaps existing transition
      const initialCandidate = { start: startTime, end: startTime };
      if (overlapsAny(initialCandidate)) {
        return; // Don't start drawing if we're starting on an existing transition
      }

      // Do not set a draft yet; only after user drags beyond threshold
      setDraft(null);

      const onMove = (ev) => {
        const [mx2] = d3.pointer(ev, svgEl);
        const endTime = xScale.invert(Math.min(plotWidth, Math.max(0, mx2 - margin.left)));
        const candidate = { start: startTime, end: endTime };
        const durationMs = Math.abs(endTime.getTime() - startTime.getTime());
        // Only update draft if it doesn't overlap and meets min pixel width
        if (!overlapsAny(candidate) && durationMs >= minDurationMs) {
          setDraft({ start: startTime, end: endTime });
        } else {
          setDraft(null);
        }
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        const currentDraft = draftRef.current;
        if (!currentDraft) {
          setDraft(null);
          return;
        }
        const startMs = currentDraft.start.getTime();
        const endMs = currentDraft.end.getTime();
        if (Math.abs(endMs - startMs) < minDurationMs) {
          setDraft(null);
          return;
        } // too small
        const normalized = {
          start: new Date(Math.min(startMs, endMs)),
          end: new Date(Math.max(startMs, endMs)),
        };
        // prevent overlaps
        if (overlapsAny(normalized)) {
          setDraft(null);
          return;
        }
        const id = `t-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        // Determine next unique transition index by scanning existing names
        const parseIndex = (n) => {
          try {
            const m = String(n || '').match(/^\s*Transition\s+(\d+)\s*$/i);
            return m ? Number(m[1]) : null;
          } catch {
            return null;
          }
        };
        const currentMax = (Array.isArray(transitions) ? transitions : []).reduce((max, t) => {
          const idx = parseIndex(t?.name);
          return Number.isFinite(idx) && idx > max ? idx : max;
        }, 0);
        const name = `Transition ${currentMax + 1}`;
        addTransition(chamber, { id, name, start: normalized.start, end: normalized.end });
        setSelectedTransition(chamber, id);
        setDraft(null);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    });

    // Resize via handles - exclude draft transitions
    svg
      .selectAll('g.transition')
      .filter(function () {
        return d3.select(this).attr('data-id') !== '__draft__';
      })
      .selectAll('rect.resize-handle')
      .on('mousedown', function (event) {
        if (transitionsLocked) return; // locked: disable resize
        const parent = d3.select(this.parentNode);
        const id = parent.attr('data-id');
        event.stopPropagation();
        event.preventDefault();
        setSelectedTransition(chamber, id);
        const t = (transitions || []).find((x) => x.id === id);
        if (!t) return;
        const side = d3.select(this).attr('data-side');
        const startInitial = new Date(t.start);
        const endInitial = new Date(t.end ?? t.start);

        const onMove = (ev) => {
          const [mx] = d3.pointer(ev, svgEl);
          const localX = Math.min(plotWidth, Math.max(0, mx - margin.left));
          if (side === 'left') {
            const xEnd = xScale(endInitial);
            const newStart = xScale.invert(Math.min(localX, xEnd - MIN_PIXEL_WIDTH));
            const candidate = { start: newStart, end: endInitial };
            if (
              !overlapsAny(candidate, id) &&
              endInitial.getTime() - newStart.getTime() >= minDurationMs
            ) {
              updateTransition(chamber, id, { start: newStart, end: endInitial });
            }
          } else if (side === 'right') {
            const xStart = xScale(startInitial);
            const newEnd = xScale.invert(Math.max(localX, xStart + MIN_PIXEL_WIDTH));
            const candidate = { start: startInitial, end: newEnd };
            if (
              !overlapsAny(candidate, id) &&
              newEnd.getTime() - startInitial.getTime() >= minDurationMs
            ) {
              updateTransition(chamber, id, { start: startInitial, end: newEnd });
            }
          }
        };
        const onUp = () => {
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
      });
  }, [
    dimensions,
    margin,
    plotWidth,
    plotHeight,
    xScale,
    yScale,
    data,
    transitions,
    draft,
    chamber,
    addTransition,
    updateTransition,
    overlapsAny,
    getSelected,
    minDurationMs,
    selectedId,
    transitionsLocked,
  ]);

  // Keep stable references to render functions for use in event handlers
  useEffect(() => {
    renderSVGRef.current = renderSVG;
  }, [renderSVG]);

  useEffect(() => {
    updateSVGAxesRef.current = updateSVGAxes;
  }, [updateSVGAxes]);

  useEffect(() => {
    updateTransitionPositionsRef.current = updateTransitionPositions;
  }, [updateTransitionPositions]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ZOOM & PAN BEHAVIOR - Custom implementation with axis-specific zoom
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl || plotWidth <= 0 || plotHeight <= 0) return;

    const svg = d3.select(svgEl);

    // Helper: Detect which region mouse is over
    const getRegion = (clientX, clientY) => {
      const rect = svgEl.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const withinPlotX = x >= margin.left && x <= margin.left + plotWidth;
      const withinPlotY = y >= margin.top && y <= margin.top + plotHeight;
      const inXAxisBand =
        withinPlotX && y >= margin.top + plotHeight && y <= margin.top + plotHeight + margin.bottom;
      const inYLeftBand = x >= 0 && x <= margin.left && withinPlotY;
      const inYRightBand = x >= margin.left + plotWidth && x <= dimensions.width && withinPlotY;
      const inPlot = withinPlotX && withinPlotY;

      if (inXAxisBand) return 'x-axis';
      if (inYLeftBand || inYRightBand) return 'y-axis';
      if (inPlot) return 'plot';
      return 'outside';
    };

    // Helper: Apply separate scale transforms to base scales
    const computeScales = (xTrans, yTrans) => {
      const [xd0, xd1] = baseXScale.domain();
      const [xr0, xr1] = baseXScale.range();
      const newXd0 = baseXScale.invert((baseXScale(xd0) - xTrans.translate) / xTrans.k);
      const newXd1 = baseXScale.invert((baseXScale(xd1) - xTrans.translate) / xTrans.k);
      const tempXScale = d3.scaleUtc().domain([newXd0, newXd1]).range([xr0, xr1]);

      const [yd0, yd1] = baseYScale.domain();
      const [yr0, yr1] = baseYScale.range();
      const newYd0 = baseYScale.invert((baseYScale(yd0) - yTrans.translate) / yTrans.k);
      const newYd1 = baseYScale.invert((baseYScale(yd1) - yTrans.translate) / yTrans.k);
      const tempYScale = d3.scaleLinear().domain([newYd0, newYd1]).range([yr0, yr1]);

      return { tempXScale, tempYScale };
    };

    // Wheel zoom handler (axis-specific when over axes, even when over plot)
    const onWheel = (event) => {
      event.preventDefault();
      const region = getRegion(event.clientX, event.clientY);
      if (region === 'outside') return;

      const rect = svgEl.getBoundingClientRect();
      const mouseX = event.clientX - rect.left - margin.left;
      const mouseY = event.clientY - rect.top - margin.top;

      const delta = -event.deltaY;
      const scaleFactor = delta > 0 ? 1.1 : 1 / 1.1;

      const newXTrans = { ...xTransformRef.current };
      const newYTrans = { ...yTransformRef.current };

      if (region === 'x-axis') {
        // Zoom only X axis
        const oldK = newXTrans.k;
        newXTrans.k = Math.max(1, Math.min(200, oldK * scaleFactor));
        newXTrans.translate = mouseX - (mouseX - newXTrans.translate) * (newXTrans.k / oldK);
        const maxTx = 0;
        const minTx = plotWidth * (1 - newXTrans.k);
        newXTrans.translate = Math.max(minTx, Math.min(maxTx, newXTrans.translate));
      } else if (region === 'y-axis') {
        // Zoom only Y axis
        const oldK = newYTrans.k;
        newYTrans.k = Math.max(1, Math.min(200, oldK * scaleFactor));
        newYTrans.translate = mouseY - (mouseY - newYTrans.translate) * (newYTrans.k / oldK);
        const maxTy = 0;
        const minTy = plotHeight * (1 - newYTrans.k);
        newYTrans.translate = Math.max(minTy, Math.min(maxTy, newYTrans.translate));
      } else if (region === 'plot') {
        // Even zoom on both axes (maintain current aspect ratio/distortion)
        const oldKx = newXTrans.k;
        const oldKy = newYTrans.k;
        newXTrans.k = Math.max(1, Math.min(200, oldKx * scaleFactor));
        newYTrans.k = Math.max(1, Math.min(200, oldKy * scaleFactor));

        newXTrans.translate = mouseX - (mouseX - newXTrans.translate) * (newXTrans.k / oldKx);
        newYTrans.translate = mouseY - (mouseY - newYTrans.translate) * (newYTrans.k / oldKy);

        const maxTx = 0;
        const minTx = plotWidth * (1 - newXTrans.k);
        newXTrans.translate = Math.max(minTx, Math.min(maxTx, newXTrans.translate));

        const maxTy = 0;
        const minTy = plotHeight * (1 - newYTrans.k);
        newYTrans.translate = Math.max(minTy, Math.min(maxTy, newYTrans.translate));
      }

      xTransformRef.current = newXTrans;
      yTransformRef.current = newYTrans;

      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(() => {
        const { tempXScale, tempYScale } = computeScales(newXTrans, newYTrans);
        const renderData = computeVisibleData(tempXScale, data, plotWidth);
        drawCanvasTrace(canvasRef.current, renderData, tempXScale, tempYScale);

        if (updateSVGAxesRef.current) {
          updateSVGAxesRef.current(tempXScale, tempYScale);
        }
        if (updateTransitionPositionsRef.current) {
          updateTransitionPositionsRef.current(tempXScale, transitions, draft, selectedId);
        }

        setXTransform(newXTrans);
        setYTransform(newYTrans);
      });
    };

    // Mouse drag handler for panning
    const onMouseDown = (event) => {
      const region = getRegion(event.clientX, event.clientY);
      if (region === 'outside') return;

      event.preventDefault();
      isDraggingRef.current = true;

      dragStartRef.current = {
        x: event.clientX,
        y: event.clientY,
        xTransform: { ...xTransformRef.current },
        yTransform: { ...yTransformRef.current },
      };

      panLockRef.current = null;
      if (region === 'x-axis') {
        panLockRef.current = 'y'; // horizontal-only pan
      } else if (region === 'y-axis') {
        panLockRef.current = 'x'; // vertical-only pan
      }
    };

    const onMouseMove = (event) => {
      if (!isDraggingRef.current) return;
      event.preventDefault();

      const dx = event.clientX - dragStartRef.current.x;
      const dy = event.clientY - dragStartRef.current.y;

      const newXTrans = { ...dragStartRef.current.xTransform };
      const newYTrans = { ...dragStartRef.current.yTransform };

      if (!panLockRef.current || panLockRef.current === 'y') {
        newXTrans.translate = dragStartRef.current.xTransform.translate + dx;
        const maxTx = 0;
        const minTx = plotWidth * (1 - newXTrans.k);
        newXTrans.translate = Math.max(minTx, Math.min(maxTx, newXTrans.translate));
      }

      if (!panLockRef.current || panLockRef.current === 'x') {
        newYTrans.translate = dragStartRef.current.yTransform.translate + dy;
        const maxTy = 0;
        const minTy = plotHeight * (1 - newYTrans.k);
        newYTrans.translate = Math.max(minTy, Math.min(maxTy, newYTrans.translate));
      }

      xTransformRef.current = newXTrans;
      yTransformRef.current = newYTrans;
      pendingXTransformRef.current = newXTrans;
      pendingYTransformRef.current = newYTrans;

      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(() => {
        const { tempXScale, tempYScale } = computeScales(newXTrans, newYTrans);
        const renderData = computeVisibleData(tempXScale, dataRef.current, plotWidth);
        drawCanvasTrace(canvasRef.current, renderData, tempXScale, tempYScale);

        const now = performance.now();
        if (now - lastAxisUpdateRef.current >= 33) {
          if (updateSVGAxesRef.current) {
            updateSVGAxesRef.current(tempXScale, tempYScale);
          }
          if (updateTransitionPositionsRef.current) {
            updateTransitionPositionsRef.current(
              tempXScale,
              transitionsRef.current,
              draftRef.current,
              selectedIdRef.current,
            );
          }
          lastAxisUpdateRef.current = now;
        }
      });
    };

    const onMouseUp = (event) => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      panLockRef.current = null;

      if (pendingXTransformRef.current && pendingYTransformRef.current) {
        setXTransform(pendingXTransformRef.current);
        setYTransform(pendingYTransformRef.current);
        pendingXTransformRef.current = null;
        pendingYTransformRef.current = null;
      }

      if (renderSVGRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = requestAnimationFrame(() => {
          renderSVGRef.current();
        });
      }
    };

    // Attach event listeners
    svgEl.addEventListener('wheel', onWheel, { passive: false });
    svgEl.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      svgEl.removeEventListener('wheel', onWheel);
      svgEl.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [
    plotWidth,
    plotHeight,
    baseXScale,
    baseYScale,
    data,
    transitions,
    draft,
    selectedId,
    dimensions,
    margin,
    computeVisibleData,
    drawCanvasTrace,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER EFFECTS - Update canvas/SVG on state changes
  // ═══════════════════════════════════════════════════════════════════════════

  // Build SVG structure when dimensions change (avoid per-frame rebuilds)
  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return;
    renderSVG();
  }, [dimensions.width, dimensions.height, renderSVG]);

  // Update canvas trace, axes, and transitions on state changes (wheel zoom / drag handled in zoom handler)
  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return;
    cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(() => {
      renderCanvas();
      updateSVGAxes(xScale, yScale);
      updateTransitionPositions(xScale, transitions, draft, selectedId);
    });
    return () => cancelAnimationFrame(rafIdRef.current);
  }, [
    xScale,
    yScale,
    renderCanvas,
    updateSVGAxes,
    updateTransitionPositions,
    transitions,
    draft,
    selectedId,
    dimensions.width,
    dimensions.height,
  ]);

  // Delete selected with keyboard and prevent selection
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onKey = (e) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        removeTransition(chamber, selectedId);
        setSelectedTransition(chamber, null);
      }
    };
    const onSelectStart = (e) => {
      e.preventDefault(); // Prevent text selection
    };
    el.addEventListener('keydown', onKey);
    el.addEventListener('selectstart', onSelectStart);
    return () => {
      el.removeEventListener('keydown', onKey);
      el.removeEventListener('selectstart', onSelectStart);
    };
  }, [selectedId, removeTransition, chamber]);

  // Controls API
  const resetZoom = useCallback(() => {
    setYDomainOverride(null);
    setXTransform({ k: 1, translate: 0 });
    setYTransform({ k: 1, translate: 0 });
    xTransformRef.current = { k: 1, translate: 0 };
    yTransformRef.current = { k: 1, translate: 0 };
  }, []);

  const resetXAxis = useCallback(() => {
    const yMin = yScale.invert(plotHeight);
    const yMax = yScale.invert(0);
    setYDomainOverride([Math.min(yMin, yMax), Math.max(yMin, yMax)]);
    setXTransform({ k: 1, translate: 0 });
    setYTransform({ k: 1, translate: 0 });
    xTransformRef.current = { k: 1, translate: 0 };
    yTransformRef.current = { k: 1, translate: 0 };
  }, [yScale, plotHeight]);

  const resetYAxis = useCallback(() => {
    if (data.length > 0) {
      const yMin = d3.min(data, (d) => d.oxygen);
      const yMax = d3.max(data, (d) => d.oxygen);
      const pad = Math.max(1e-6, (yMax - yMin) * 0.05);
      const nicified = d3
        .scaleLinear()
        .domain([yMin - pad, yMax + pad])
        .nice()
        .domain();
      setYDomainOverride(nicified);
      setXTransform({ k: 1, translate: 0 });
      setYTransform({ k: 1, translate: 0 });
      xTransformRef.current = { k: 1, translate: 0 };
      yTransformRef.current = { k: 1, translate: 0 };
    }
  }, [data]);

  const jumpToNextTransition = useCallback(() => {
    if (!transitions.length) return;
    const sorted = [...transitions].sort((a, b) => new Date(a.start) - new Date(b.start));
    let idx = -1;
    if (selectedId) {
      idx = sorted.findIndex((t) => t.id === selectedId);
    } else {
      idx = -1;
    }
    const nextIdx = (idx + 1) % sorted.length;
    const next = sorted[nextIdx];

    // Select next and reset zoom to show all marks
    setSelectedTransition(chamber, next.id);
    resetZoom();
  }, [transitions, selectedId, chamber]);

  const jumpToPreviousTransition = useCallback(() => {
    if (!transitions.length) return;
    const sorted = [...transitions].sort((a, b) => new Date(a.start) - new Date(b.start));
    let idx = -1;
    if (selectedId) {
      idx = sorted.findIndex((t) => t.id === selectedId);
    } else {
      idx = 0;
    }
    const prevIdx = (idx - 1 + sorted.length) % sorted.length;
    const prev = sorted[prevIdx];

    // Select previous and reset zoom to show all marks
    setSelectedTransition(chamber, prev.id);
    resetZoom();
  }, [transitions, selectedId, chamber]);

  const deleteSelectedTransition = useCallback(() => {
    if (!selectedId || !transitions?.length) return;
    const sorted = [...transitions].sort((a, b) => new Date(a.start) - new Date(b.start));
    const idx = sorted.findIndex((t) => t.id === selectedId);
    if (idx === -1) return;
    const remaining = sorted.filter((t) => t.id !== selectedId);
    removeTransition(chamber, selectedId);
    if (remaining.length === 0) {
      setSelectedTransition(chamber, null);
      return;
    }
    const nextIdx = idx >= remaining.length ? 0 : idx;
    const next = remaining[nextIdx];
    setSelectedTransition(chamber, next.id);
  }, [selectedId, transitions, removeTransition, chamber, setSelectedTransition]);

  useEffect(() => {
    if (controlsRef) {
      controlsRef.current = {
        resetZoom,
        resetXAxis,
        resetYAxis,
        jumpToNextTransition,
        jumpToPreviousTransition,
        deleteSelectedTransition,
        setTransitionsLocked,
      };
    }
  }, [
    controlsRef,
    resetZoom,
    resetXAxis,
    resetYAxis,
    jumpToNextTransition,
    jumpToPreviousTransition,
    deleteSelectedTransition,
  ]);

  if (!currentFileData) {
    return (
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        No data
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      tabIndex={0}
      sx={{
        position: 'relative',
        width: '100%',
        height,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
      }}
    >
      {/* {isLoading && (
        <Box sx={{
          position: 'absolute',
          zIndex: 2,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
          pointerEvents: 'none',
        }}>
          <CircularProgress size={24} sx={{ mb: 1, color: '#394B57' }} />
          <Typography variant="body1">Loading marks</Typography>
        </Box>
      )} */}
      <svg
        ref={svgRef}
        focusable="false"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 0,
          outline: 'none', // Remove focus outline
          border: 'none', // Remove any border
        }}
      />
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', top: 0, left: 0, zIndex: 1, pointerEvents: 'none' }}
      />
    </Box>
  );
};

export default O2kChartView;
